import { useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { TaskTypeSchema } from "@myapp/contract";
import PageLayout from "../../../shared/ui/layouts/PageLayout/PageLayout";
import { routes } from "../../../shared/config/routes";
import { getTaskTypeLabel, useCreateTask } from "../../../entities/task";
import { SuccessDialog } from "../../../shared/ui/feedback/SuccessDialog/SuccessDialog";
import { useSuccessDialog } from "../../../shared/hooks/useSuccessDialog";
import {
  TaskFormWidget,
  toCreateDto,
  getDefaultValues,
  type TaskFormValues,
} from "../../../features/aufgaben";

function getTodayKey(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function AufgabenCreatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const rawType = searchParams.get("taskType");
  const parsed = TaskTypeSchema.safeParse(rawType);
  const taskType = parsed.success ? parsed.data : "GENERAL_TASK" as const;

  const pendingAction = useRef<"save" | "plan">("save");
  const submitRef = useRef<(() => void) | null>(null);
  const createMutation = useCreateTask();
  const { showSuccess, dialogProps } = useSuccessDialog();

  if (!parsed.success) {
    navigate(routes.aufgaben.root, { replace: true });
    return null;
  }

  const handleValidate = (values: TaskFormValues) => {
    if (pendingAction.current === "save" && !values.assignedTechnicianId) {
      return { assignedTechnicianId: "Pflichtfeld" };
    }
    return null;
  };

  const handleSubmit = async (values: TaskFormValues) => {
    const dto = toCreateDto(values);
    const task = await createMutation.mutateAsync(dto);

    if (pendingAction.current === "plan") {
      const rawDate = values.plannedStart || "";
      const dayKey = rawDate ? rawDate.substring(0, 10) : getTodayKey();
      navigate(`${routes.aufgaben.plan}?taskId=${task.id}&day=${dayKey}`);
    } else {
      showSuccess("Aufgabe erfolgreich erstellt", () =>
        navigate(routes.aufgaben.root)
      );
    }
  };

  const isErrands = taskType === "ERRANDS";

  return (
    <>
      <PageLayout
        pageTitle={"Neue " + getTaskTypeLabel(taskType)}
        scrollable
        back
        backTo={routes.aufgaben.root}
        primaryAction={{
          label: isErrands ? "Speichern" : "Im Kalender planen",
          onClick: () => {
            pendingAction.current = isErrands ? "save" : "plan";
            submitRef.current?.();
          },
          loading: createMutation.isPending,
        }}
        secondaryAction={isErrands ? {
          label: "Im Kalender planen",
          onClick: () => {
            pendingAction.current = "plan";
            submitRef.current?.();
          },
          loading: createMutation.isPending,
        } : undefined}
      >
        <TaskFormWidget
          taskType={taskType}
          onSubmit={handleSubmit}
          isLoading={createMutation.isPending}
          submitRef={submitRef}
          initialValues={getDefaultValues(taskType)}
          mode="create"
          onValidate={handleValidate}
        />
      </PageLayout>

      <SuccessDialog
        open={dialogProps.open}
        onOpenChange={(open) => {
          if (!open) dialogProps.onClose();
        }}
        title={dialogProps.title}
        onClose={dialogProps.onClose}
      />
    </>
  );
}
